import type { FeedbackType } from './messages';

type ValueOf<T> = T[keyof T];

export const TICKET_CATEGORY = {
  CHANGEABLE: 'changeable',
  UNCHANGEABLE: 'unchangeable'
} as const;

export const PROPOSAL_BUNDLE_CATEGORY = {
  TRANSPORT: 'transport',
  HOTEL: 'hotel',
  PACKAGE: 'package',
  OUTWARD: 'outward',
  HOMEWARD: 'homeward'
} as const;

export type TicketCategory = ValueOf<typeof TICKET_CATEGORY>;
export type PreferredTransportType = 'rail' | 'air';
export type ProposalBundleState = 'suggesting' | 'selected' | 'rejected';
export type ProposalBundleCategory = ValueOf<typeof PROPOSAL_BUNDLE_CATEGORY>;
export type SummaryItemCategory = Extract<
  ProposalBundleCategory,
  | typeof PROPOSAL_BUNDLE_CATEGORY.OUTWARD
  | typeof PROPOSAL_BUNDLE_CATEGORY.HOMEWARD
  | typeof PROPOSAL_BUNDLE_CATEGORY.HOTEL
>;
export type ForeignItinerary = 'one_way' | 'round_trip';

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  [TICKET_CATEGORY.CHANGEABLE]: '変更可',
  [TICKET_CATEGORY.UNCHANGEABLE]: '変更不可'
} as const;

export const PROPOSAL_BUNDLE_STATE = {
  SUGGESTING: 'suggesting',
  SELECTED: 'selected',
  REJECTED: 'rejected'
} as const;

export const PROPOSAL_BUNDLE_STATE_LABELS: Record<ProposalBundleState, string> = {
  [PROPOSAL_BUNDLE_STATE.SUGGESTING]: '提案中',
  [PROPOSAL_BUNDLE_STATE.SELECTED]: '選択済み',
  [PROPOSAL_BUNDLE_STATE.REJECTED]: '予約しない'
} as const;

export const PROPOSAL_BUNDLE_CATEGORY_LABELS: Record<ProposalBundleCategory, string> = {
  [PROPOSAL_BUNDLE_CATEGORY.OUTWARD]: '行き',
  [PROPOSAL_BUNDLE_CATEGORY.HOMEWARD]: '帰り',
  [PROPOSAL_BUNDLE_CATEGORY.TRANSPORT]: '交通',
  [PROPOSAL_BUNDLE_CATEGORY.HOTEL]: 'ホテル',
  [PROPOSAL_BUNDLE_CATEGORY.PACKAGE]: 'パッケージ'
} as const;

export const TICKET_TYPE_FOREIGN = 'foreign' as const;

export type ProposalItemTransport = {
  departureTime: string | null;
  arrivalTime: string | null;
  departureDate: string | null;
  arrivalDate: string | null;
  flightName: string | null;
  isConnectionFlight: boolean;
  connectionSummary: string | null;
  seatClass: string | null;
  ticketType: string | null;
  foreignItinerary: ForeignItinerary | null;
  ticketCategory: TicketCategory | null;
  isAir: boolean | null;
  isShinkansen: boolean | null;
  segmentRouteLabel: string | null;
  airlineDisplay: string | null;
  roundTripDurationLabel: string | null;
  legDurationSummary: string | null;
  roundTripFlightId: string | null;
  foreignRoundTripDisplayKey: string;
};

export type ProposalItemHotelSegment = {
  index: number | null;
  checkin: string | null;
  checkout: string | null;
};

export type ProposalItemHotel = {
  stayDays: number | null;
  breakfastExist: boolean | null;
  smoke: boolean | null;
  segment: ProposalItemHotelSegment;
};

type ProposalItemBase = {
  id: number;
  title: string | null;
  subtitle: string | null;
  price: number | null;
  priceWithTax: number | null;
  priceText: string | null;
  aiHighlightTags: string[];
  selected: boolean;
};

export type TransportProposalItem = ProposalItemBase & {
  transport: ProposalItemTransport;
  hotel: null;
};

export type HotelProposalItem = ProposalItemBase & {
  transport: null;
  hotel: ProposalItemHotel;
};

export type ProposalItem = TransportProposalItem | HotelProposalItem;

export const hasProposalItemTransport = (item: ProposalItem): item is TransportProposalItem =>
  item.transport !== null;

export const hasProposalItemHotel = (item: ProposalItem): item is HotelProposalItem => item.hotel !== null;

export const filterTransportItems = (items: ProposalItem[]): TransportProposalItem[] =>
  items.filter(hasProposalItemTransport);

export const filterHotelItems = (items: ProposalItem[]): HotelProposalItem[] => items.filter(hasProposalItemHotel);

export const findSelectedTransportItem = (items: ProposalItem[]): TransportProposalItem | null =>
  items.find((item): item is TransportProposalItem => item.selected && hasProposalItemTransport(item)) ?? null;

export const findTransportItemById = (items: ProposalItem[], itemId: number): TransportProposalItem | null =>
  items.find((item): item is TransportProposalItem => item.id === itemId && hasProposalItemTransport(item)) ??
  null;

export const findHotelItemById = (items: ProposalItem[], itemId: number): HotelProposalItem | null =>
  items.find((item): item is HotelProposalItem => item.id === itemId && hasProposalItemHotel(item)) ?? null;

export type ProposalBundleRoute = {
  origin: string | null;
  destination: string | null;
  departureDate: string | null;
  returnDate: string | null;
  stayDays: number | null;
  preferredTransportType: PreferredTransportType | null;
};

export type ProposalBundlePartition = {
  partitioned: boolean;
  segmentCount: number | null;
  allSegmentsSelected?: boolean | null;
};

type ProposalBundleBase = {
  id: number;
  state: ProposalBundleState;
  route: ProposalBundleRoute;
  feedback: FeedbackType | null;
  feedbackCommentSubmitted: boolean;
};

export type TransportProposalBundleCategory = Extract<
  ProposalBundleCategory,
  | typeof PROPOSAL_BUNDLE_CATEGORY.OUTWARD
  | typeof PROPOSAL_BUNDLE_CATEGORY.HOMEWARD
  | typeof PROPOSAL_BUNDLE_CATEGORY.TRANSPORT
  | typeof PROPOSAL_BUNDLE_CATEGORY.PACKAGE
>;

export type TransportProposalBundle = ProposalBundleBase & {
  category: TransportProposalBundleCategory;
  hasForeignFlightRoundTrip: boolean | null;
  hasForeignFlight: boolean | null;
  items: TransportProposalItem[];
};

export type HotelProposalBundle = ProposalBundleBase & {
  category: typeof PROPOSAL_BUNDLE_CATEGORY.HOTEL;
  partition: ProposalBundlePartition;
  items: HotelProposalItem[];
};

export type HotelSegmentGroup = {
  index: number;
  checkin: string | null;
  checkout: string | null;
  items: HotelProposalItem[];
};

export type ProposalBundle = TransportProposalBundle | HotelProposalBundle;

export const isTransportProposalBundle = (bundle: ProposalBundle): bundle is TransportProposalBundle =>
  bundle.category !== PROPOSAL_BUNDLE_CATEGORY.HOTEL;

export const isHotelProposalBundle = (bundle: ProposalBundle): bundle is HotelProposalBundle =>
  bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOTEL;
